import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from './ui/Icons';
import { useAuthModal } from '../contexts/AuthContext';

export const CreatorLanding: React.FC = () => {
  const navigate = useNavigate();
  const { openAuthModal } = useAuthModal();
  const [activeFeature, setActiveFeature] = useState(0);

  const aiFeatures = [
    {
      title: 'Create Full Studies with AI',
      description: 'Generate complete Bible studies in minutes—content, images, and structure',
      icon: Icons.Sparkles,
      details: ['Enter a topic and title, AI does the rest', 'Generates multiple segments automatically', 'Creates cover image and section imagery', 'Edit and customize the generated study'],
      color: 'from-indigo-600 to-purple-600'
    },
    {
      title: 'AI Image Generation',
      description: 'Create stunning biblical imagery in seconds with 12 artistic styles',
      icon: Icons.Image,
      details: ['Renaissance & classical art styles', 'Stained glass & mosaic effects', 'Oil painting & watercolor', 'Multiple aspect ratios'],
      color: 'from-amber-500 to-orange-600'
    },
    {
      title: 'AI Video Creation',
      description: 'Generate cinematic video content with Google Veo technology',
      icon: Icons.Youtube,
      details: ['6 video styles (cinematic, animation, etc.)', 'Customizable duration & resolution', 'Reference image support', 'Professional quality output'],
      color: 'from-red-500 to-pink-600'
    },
    {
      title: 'Text-to-Speech',
      description: 'Natural voice narration with multiple voice options',
      icon: Icons.Audio,
      details: ['Multiple natural-sounding voices', 'Perfect for accessibility', 'Auto-play during studies', 'High-quality audio output'],
      color: 'from-blue-500 to-indigo-600'
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 overflow-x-hidden">

      {/* Hero Section - Emotional, Benefit-Focused */}
      <section className="relative pt-12 pb-20 lg:pt-20 lg:pb-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-blue-100">
                <Icons.Star className="w-4 h-4 text-amber-500" />
                <span>Free to Get Started</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-gray-900 mb-6 leading-tight">
                Your Church Deserves
                <span className="block text-primary mt-2">Modern Bible Studies</span>
              </h1>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Create beautiful, interactive studies in minutes—not hours.
                AI-powered tools handle the heavy lifting so you can focus on what matters: <strong>your message</strong>.
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <button
                  onClick={() => openAuthModal('signup', 'teacher')}
                  className="w-full sm:w-auto px-8 py-4 bg-primary text-white text-lg font-bold rounded-xl shadow-lg hover:shadow-xl hover:bg-blue-800 transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2 group"
                >
                  Create Free Account
                  <Icons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="text-sm text-gray-500">No credit card required</p>
              </div>
            </div>

            {/* Hero Visual - Study Preview Mockup */}
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl transform rotate-3 scale-105"></div>
              <div className="relative bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="space-y-4">
                  <div className="h-40 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center">
                    <Icons.Image className="w-16 h-16 text-amber-400" />
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-100 rounded w-full"></div>
                    <div className="h-4 bg-gray-100 rounded w-5/6"></div>
                  </div>
                  <div className="flex gap-2">
                    <div className="h-10 bg-primary/10 rounded-lg flex-1 flex items-center justify-center">
                      <Icons.BookOpen className="w-5 h-5 text-primary" />
                    </div>
                    <div className="h-10 bg-green-100 rounded-lg flex-1 flex items-center justify-center">
                      <Icons.Quiz className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="h-10 bg-purple-100 rounded-lg flex-1 flex items-center justify-center">
                      <Icons.Youtube className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Background Decorations */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-blue-50 to-transparent -z-10"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-purple-100 to-transparent rounded-full -z-10 blur-3xl opacity-50"></div>
      </section>

      {/* Social Proof Bar */}
      <section className="py-8 bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16 text-center">
            <div>
              <div className="text-3xl font-bold text-primary">100%</div>
              <div className="text-sm text-gray-500">Free to Start</div>
            </div>
            <div className="hidden sm:block w-px h-12 bg-gray-300"></div>
            <div>
              <div className="text-3xl font-bold text-primary">4</div>
              <div className="text-sm text-gray-500">AI Tools Included</div>
            </div>
            <div className="hidden sm:block w-px h-12 bg-gray-300"></div>
            <div>
              <div className="text-3xl font-bold text-primary">Minutes</div>
              <div className="text-sm text-gray-500">To Create Studies</div>
            </div>
            <div className="hidden sm:block w-px h-12 bg-gray-300"></div>
            <div>
              <div className="text-3xl font-bold text-primary">Unlimited</div>
              <div className="text-sm text-gray-500">Team Members</div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-6">
            Stop Spending Hours on Study Materials
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
            You have a message to share. Let our AI tools create professional visuals,
            engaging quizzes, and beautiful layouts while you focus on teaching.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Icons.Clock className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold mb-3">Save Hours Weekly</h3>
              <p className="text-gray-600">AI generates images and videos in seconds. No design skills needed.</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Icons.Users className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-bold mb-3">Engage Your Congregation</h3>
              <p className="text-gray-600">Interactive quizzes, Q&A, and gamification keep members coming back.</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Icons.Globe className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold mb-3">Reach Anyone, Anywhere</h3>
              <p className="text-gray-600">Share studies publicly or keep them private for your organization.</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Features Showcase */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Icons.Flame className="w-4 h-4 text-amber-400" />
              <span>Powered by Google AI</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">
              AI That Makes You Look Professional
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Four powerful AI tools at your fingertips—no technical knowledge required.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Feature Selector */}
            <div className="space-y-4">
              {aiFeatures.map((feature, index) => (
                <button
                  key={index}
                  onClick={() => setActiveFeature(index)}
                  className={`w-full text-left p-6 rounded-2xl transition-all ${
                    activeFeature === index
                      ? 'bg-white text-gray-900 shadow-xl'
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      activeFeature === index
                        ? `bg-gradient-to-br ${feature.color} text-white`
                        : 'bg-white/10 text-white'
                    }`}>
                      <feature.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">{feature.title}</h3>
                      <p className={activeFeature === index ? 'text-gray-600' : 'text-gray-400'}>
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Feature Details */}
            <div className="bg-white/10 rounded-3xl p-8 backdrop-blur">
              <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-6 bg-gradient-to-br ${aiFeatures[activeFeature].color}`}>
                {React.createElement(aiFeatures[activeFeature].icon, { className: "w-10 h-10 text-white" })}
              </div>
              <h3 className="text-2xl font-bold mb-4">{aiFeatures[activeFeature].title}</h3>
              <ul className="space-y-3">
                {aiFeatures[activeFeature].details.map((detail, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Icons.Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-200">{detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Simplified */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
              Up and Running in 3 Steps
            </h2>
            <p className="text-xl text-gray-600">
              Get your church set up in under 5 minutes
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connector Line */}
            <div className="hidden md:block absolute top-16 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-blue-200 via-purple-200 to-green-200"></div>

            <div className="relative text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold shadow-lg relative z-10">
                1
              </div>
              <h3 className="text-xl font-bold mb-3">Create Your Organization</h3>
              <p className="text-gray-600">Sign up, name your church or ministry, and get your unique invite code instantly.</p>
            </div>

            <div className="relative text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold shadow-lg relative z-10">
                2
              </div>
              <h3 className="text-xl font-bold mb-3">Build Your First Study</h3>
              <p className="text-gray-600">Use "Create with AI" to generate a full study instantly, or build one manually with our easy editor.</p>
            </div>

            <div className="relative text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold shadow-lg relative z-10">
                3
              </div>
              <h3 className="text-xl font-bold mb-3">Invite Your Members</h3>
              <p className="text-gray-600">Share your code. Members join and instantly access all your private content.</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => openAuthModal('signup', 'teacher')}
              className="px-8 py-4 bg-primary text-white text-lg font-bold rounded-xl shadow-lg hover:shadow-xl hover:bg-blue-800 transition-all inline-flex items-center gap-2"
            >
              Get Started Free <Icons.ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Content Types Grid */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
              Everything You Need to Teach
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Mix and match these building blocks to create engaging, interactive studies
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Icons.Edit, title: 'Text & Images', desc: 'Rich content with AI imagery', color: 'bg-blue-500' },
              { icon: Icons.BookOpen, title: 'Bible Verses', desc: 'Input your own texts', color: 'bg-green-500' },
              { icon: Icons.Youtube, title: 'Videos', desc: 'YouTube or AI-generated', color: 'bg-red-500' },
              { icon: Icons.Quiz, title: 'Quizzes', desc: 'Test & reinforce learning', color: 'bg-indigo-500' },
              { icon: Icons.MessageSquare, title: 'Q&A Prompts', desc: 'Engage in discussion', color: 'bg-purple-500' },
              { icon: Icons.Megaphone, title: 'Call to Action', desc: 'Collect signups & RSVPs', color: 'bg-amber-500' },
              { icon: Icons.Audio, title: 'Audio Narration', desc: 'AI generated', color: 'bg-teal-500' },
              { icon: Icons.List, title: 'Courses', desc: 'Multi-study journeys', color: 'bg-pink-500' },
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all group">
                <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Admin Features */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Icons.Shield className="w-4 h-4" />
                <span>Powerful Admin Tools</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-6">
                Manage Your Ministry with Ease
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Everything you need to run a thriving digital ministry—member management,
                analytics, notifications, and more.
              </p>

              <div className="space-y-4">
                {[
                  { icon: Icons.Users, title: 'Member Management', desc: 'Invite members, manage roles, track engagement' },
                  { icon: Icons.MessageSquare, title: 'Response Center', desc: 'View questions, CTA responses, and analytics' },
                  { icon: Icons.Mail, title: 'Email Notifications', desc: 'Automated emails for completions and replies' },
                  { icon: Icons.BarChart, title: 'Usage Analytics', desc: 'Track AI usage and member activity' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 rounded-3xl transform -rotate-3"></div>
              <div className="relative bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-bold text-gray-900">Response Center</h4>
                  <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full">12 new</span>
                </div>
                <div className="space-y-4">
                  {[
                    { type: 'Question', name: 'Sarah M.', time: '2m ago', icon: Icons.MessageSquare, color: 'bg-purple-100 text-purple-600' },
                    { type: 'Email Signup', name: 'john@email.com', time: '15m ago', icon: Icons.Mail, color: 'bg-blue-100 text-blue-600' },
                    { type: 'Event RSVP', name: 'David K.', time: '1h ago', icon: Icons.Calendar, color: 'bg-amber-100 text-amber-600' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.color}`}>
                        <item.icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{item.type}</div>
                        <div className="text-xs text-gray-500">{item.name}</div>
                      </div>
                      <div className="text-xs text-gray-400">{item.time}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Start free with everything you need. Upgrade when you want more AI power.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Tier */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 relative">
              <div className="absolute -top-3 left-6">
                <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">
                  Most Popular
                </span>
              </div>
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Free Forever</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-gray-900">$0</span>
                  <span className="text-gray-500">/forever</span>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <Icons.Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-gray-900">Unlimited Bible study members</span>
                    <p className="text-sm text-gray-500">Invite your entire congregation</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Icons.Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-gray-900">Unlimited studies & courses</span>
                    <p className="text-sm text-gray-500">Create as many as you need</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Icons.Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-gray-900">All content types included</span>
                    <p className="text-sm text-gray-500">Quizzes, Q&A, CTAs, verses & more</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Icons.Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-gray-900">AI Starter Credits</span>
                    <p className="text-sm text-gray-500">50 images, 250 audio, 5 videos to try AI</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => openAuthModal('signup', 'teacher')}
                className="w-full py-3 px-6 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors"
              >
                Get Started Free
              </button>
            </div>

            {/* Ministry Tier */}
            <div className="bg-gradient-to-br from-primary to-blue-700 rounded-2xl shadow-xl p-8 text-white relative">
              <div className="absolute -top-3 right-6">
                <span className="bg-amber-400 text-amber-900 text-xs font-bold px-3 py-1 rounded-full">
                  Best Value
                </span>
              </div>
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2">Ministry</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold">$24.99</span>
                  <span className="text-blue-200">/month</span>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <Icons.Check className="w-5 h-5 text-green-300 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Everything in Free, plus...</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Icons.Check className="w-5 h-5 text-green-300 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">250 AI images per month</span>
                    <p className="text-sm text-blue-200">Refreshes monthly</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Icons.Check className="w-5 h-5 text-green-300 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">1,000 AI audio per month</span>
                    <p className="text-sm text-blue-200">Natural voice narration</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Icons.Check className="w-5 h-5 text-green-300 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">10 AI videos per month</span>
                    <p className="text-sm text-blue-200">Cinematic video generation</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Icons.Check className="w-5 h-5 text-green-300 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Priority support</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => openAuthModal('signup', 'teacher')}
                className="w-full py-3 px-6 bg-white text-primary font-bold rounded-xl hover:bg-gray-100 transition-colors"
              >
                Start Free, Upgrade Later
              </button>
              <p className="text-center text-sm text-blue-200 mt-3">
                Try free first, upgrade when you need more AI
              </p>
            </div>
          </div>

          {/* Key Message */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-full px-6 py-3">
              <Icons.Users className="w-5 h-5 text-primary" />
              <span className="text-gray-700">
                <strong className="text-gray-900">Unlimited members on all plans</strong> — your whole church can join
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4">
              Common Questions
            </h2>
          </div>

          <div className="space-y-4">
            {[
              { q: 'Is it really free?', a: 'Yes! Creating studies, inviting members, and all core features are 100% free forever. You get starter AI credits to try the AI features, and can upgrade to Ministry tier for monthly rolling AI credits when you need more.' },
              { q: 'How many people can join my organization?', a: 'Unlimited! There\'s no limit on team members or study participants on any plan. Invite your entire congregation.' },
              { q: 'What happens when I use all my free AI credits?', a: 'Your free starter credits (50 images, 250 audio, 5 videos) are one-time. Once used, you can upgrade to Ministry tier for monthly rolling credits, or continue using the platform without AI generation.' },
              { q: 'Do I need technical skills?', a: 'Not at all. Our editor is designed for pastors and teachers, not developers. If you can write an email, you can create a study.' },
              { q: 'Can I cancel anytime?', a: 'Absolutely. Ministry tier is month-to-month with no contracts. Cancel anytime and keep access to all your content.' },
              { q: 'Is the content private?', a: 'You control visibility. Keep studies private for just your organization, share with the community, or make them fully public.' },
            ].map((item, i) => (
              <details key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 group">
                <summary className="flex items-center justify-between p-6 cursor-pointer font-bold text-gray-900 hover:text-primary transition-colors">
                  {item.q}
                  <Icons.ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-primary via-blue-700 to-indigo-800 px-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">
            Ready to Transform Your Teaching?
          </h2>
          <p className="text-xl mb-10 opacity-90 max-w-2xl mx-auto">
            Join churches and ministries already using Bible Aware to create
            engaging, beautiful Bible studies. Start free today.
          </p>

          <button
            onClick={() => openAuthModal('signup', 'teacher')}
            className="px-10 py-5 bg-white text-primary text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl hover:bg-gray-50 transition-all hover:-translate-y-1 inline-flex items-center gap-3 group"
          >
            Create Your Free Account
            <Icons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>

          <p className="text-sm mt-6 opacity-75">
            Takes less than 2 minutes to set up
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-3">
            <div className="bg-gray-800 p-2 rounded-lg">
              <Icons.BookOpen className="w-6 h-6 text-white" />
            </div>
            <span className="font-serif font-bold text-xl">Bible Aware</span>
          </div>

          <div className="flex flex-wrap justify-center gap-8 text-sm font-medium text-gray-400">
            <button onClick={() => navigate('/')} className="hover:text-white transition-colors">For Students</button>
            <button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy</button>
          </div>

          <div className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Bible Aware. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};
